import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { paths } from "./paths";
import Appointments from "../screens/appointments";
import Availability from "../screens/availability";
import Employee from "../screens/employee";
import Employees from "../screens/employees";
import EmployeeServices from "../screens/employeeServices";
import Login from "../screens/login";
import Profile from "../screens/profile";
import Clients from "../screens/clients";
import ClientHistory from "../screens/clientHistory";
import Settings from "../screens/settings";
import CreateAppointment from "../screens/createAppointment";
import WorkingHours from "../screens/workingHours";
import Memberships from "../screens/memberships";
import ClientMembership from "../screens/clientMembership";
import Services from "../screens/services";
import ServiceCreate from "../screens/serviceCreate";
import Subscription from "../screens/subscription";
import Invoices from "../screens/invoices";
import Documents from "../screens/documents";
import Contact from "../screens/contact";
import Management from "../screens/management";
import Notifications from "../screens/notifications";
import CreateNotifications from "../screens/createNotifications";
import MessageTemplates from "../screens/messageTemplates";
import MessageHistory from "../screens/messageHistory";
import Statistics from "../screens/statistics";
import Class from "../screens/class";
import PaymentSettings from "../screens/paymentSettings";
import ClientBilling from "../screens/clientBilling";
import Forms from "../screens/forms";
import PaymentRequests from "../screens/paymentRequests";
import Automations from "../screens/automations";
import AppointmentRequests from "../screens/appointmentRequests";
import ScheduleOverride from "../screens/scheduleOverride";
import Coupons from "../screens/coupons";
import GiftCards from "../screens/giftCards";
import GiftCardsPurchaseHistory from "../screens/giftCardsPurchaseHistory";
import GiftCardsConsumptionHistory from "../screens/giftCardsConsumptionHistory";
import CouponUsageHistory from "../screens/couponUsageHistory";
import GiftCardConsumptionHistoryByCode from "../screens/giftCardConsumptionHistoryByCode";
import Reminders from "../screens/reminders";
import OpenfrmtExport from "../screens/openfrmtExport";
import Products from "../screens/products";
import Reviews from "../screens/reviews";
import Purchases from "../screens/purchases";
import AllMemberships from "../screens/allMemberships";
import CashRegister from "../screens/cashRegister";
import WhatsAppBot from "../screens/whatsappBot";
import AiReceptionistSettings from "../screens/aiReceptionistSettings";
import VoiceAssistant from "../screens/voiceAssistant";

// ── App-entry promo popup ────────────────────────────────────────────
// Mounted once below, inside the Router: it needs router context and has
// to survive the Layout remount that every navigation causes.
import EntryPopup from "../components/EntryPopup";

// ── Guided Tour ──────────────────────────────────────────────────────
// Mounted once, at the router scope, so the Provider state survives
// navigation. (Layout is re-mounted per screen and can't host state
// that needs to live across routes.)
import { useEffect, useRef } from "react";
import GuidedTourProvider, { useGuidedTour } from "../guidedTour/GuidedTourProvider";
import TourPlayer from "../guidedTour/components/TourPlayer";
import WelcomeModal from "../guidedTour/components/WelcomeModal";
import TourListSheet from "../guidedTour/components/TourListSheet";

const TourAutoStarter = () => {
  const { progress, openWelcome, welcomeOpen, activeTour } = useGuidedTour();
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    if (!progress.welcomeSeen && !welcomeOpen && !activeTour) {
      fired.current = true;
      const id = setTimeout(() => openWelcome(), 600);
      return () => clearTimeout(id);
    }
  }, [progress.welcomeSeen, openWelcome, welcomeOpen, activeTour]);
  return null;
};

// The tour avatar lives inline inside the Header now (header.jsx mounts
// it directly), so it's only ever visible on authenticated screens that
// have a header. What we still need globally is the player + the
// list/welcome modal overlays + the welcome auto-starter — and those
// only make sense once the user is past /auth/*.
const TourOverlays = () => {
  const { pathname } = useLocation();
  const isAuthRoute = pathname.startsWith("/auth");
  if (isAuthRoute) return <TourPlayer />;
  return (
    <>
      <TourPlayer />
      <WelcomeModal />
      <TourListSheet />
      <TourAutoStarter />
    </>
  );
};

const AppRoutes = () => {
  return (
    <Router>
      <GuidedTourProvider>
      <Routes>
        <Route exact path="/" element={<Navigate to={paths.appointments} replace />} />
        <Route exact path={paths.management} element={<Management />} />

        <Route exact path={paths.stats} element={<Statistics />} />

        <Route exact path={paths.appointments} element={<Appointments />} />

        <Route exact path={paths.settings} element={<Settings />} />

        <Route
          exact
          path={paths.createAppointment}
          element={<CreateAppointment />}
        />

        <Route exact path={paths.login} element={<Login />} />

        <Route exact path={paths.clients} element={<Clients />} />

        <Route
          exact
          path={paths.clientMemberShip(":id")}
          element={<ClientMembership />}
        />

        <Route
          exact
          path={paths.clientHistory(":id")}
          element={<ClientHistory />}
        />

        <Route
          exact
          path={paths.workingHours(":id")}
          element={<WorkingHours />}
        />

        <Route exact path={paths.memberShips} element={<Memberships />} />

        <Route exact path={paths.employees} element={<Employees />} />

        <Route exact path={paths.services} element={<Services />} />

        <Route exact path={paths.createService} element={<ServiceCreate />} />

        <Route
          exact
          path={paths.editService(":serviceId")}
          element={<ServiceCreate />}
        />

        <Route
          exact
          path={paths.class(":appointmentId", ":employeeId")}
          element={<Class />}
        />

        <Route
          exact
          path={paths.createNotifications}
          element={<CreateNotifications />}
        />

        <Route exact path={paths.notifications} element={<Notifications />} />

        <Route exact path={paths.messageTemplates} element={<MessageTemplates />} />

        <Route exact path={paths.messageHistory} element={<MessageHistory />} />

        <Route exact path={paths.profile} element={<Profile />} />

        <Route exact path={paths.subscription} element={<Subscription />} />

        <Route exact path={paths.invoices} element={<Invoices />} />
        <Route exact path={paths.documents} element={<Documents />} />

        <Route exact path={paths.contact} element={<Contact />} />

        <Route exact path={paths.forms} element={<Forms />} />

        <Route exact path={paths.paymentRequests} element={<PaymentRequests />} />

        <Route exact path={paths.automations} element={<Automations />} />

        <Route exact path={paths.appointmentRequests} element={<AppointmentRequests />} />

        <Route exact path={paths.scheduleOverride} element={<ScheduleOverride />} />

        <Route exact path={paths.paymentSettings} element={<PaymentSettings />} />

        <Route
          exact
          path={paths.clientBilling(":id")}
          element={<ClientBilling />}
        />

        <Route exact path={paths.coupons} element={<Coupons />} />

        <Route
          exact
          path={paths.couponsUsageHistory}
          element={<CouponUsageHistory />}
        />

        <Route exact path={paths.giftCards} element={<GiftCards />} />

        <Route
          exact
          path={paths.giftCardPurchaseHistory}
          element={<GiftCardsPurchaseHistory />}
        />

        <Route
          exact
          path={paths.giftCardConsumptionHistoryByCode(":code")}
          element={<GiftCardConsumptionHistoryByCode />}
        />

        <Route
          exact
          path={paths.giftCardConsumptionHistory}
          element={<GiftCardsConsumptionHistory />}
        />

        <Route exact path={paths.reminders} element={<Reminders />} />

        <Route exact path={paths.openfrmtExport} element={<OpenfrmtExport />} />

        <Route exact path={paths.products} element={<Products />} />

        <Route exact path={paths.reviews} element={<Reviews />} />

        <Route exact path={paths.purchases} element={<Purchases />} />

        <Route exact path={paths.allMemberships} element={<AllMemberships />} />

        <Route exact path={paths.cashRegister} element={<CashRegister />} />

        <Route exact path={paths.whatsappBot} element={<WhatsAppBot />} />

        {/* Literal paths — these two screens address themselves by string
            (whatsappBot.jsx's AI_SETTINGS_PATH, the header's "עוזר קולי"
            entry) rather than through paths.js. */}
        <Route
          exact
          path="/whatsapp-bot/ai-settings"
          element={<AiReceptionistSettings />}
        />

        <Route exact path="/voice-assistant" element={<VoiceAssistant />} />
      </Routes>
      {/* Tour overlays — sit on top of whichever route is active. */}
      <TourOverlays />
      {/* App-entry promo popup — renders nothing unless a fresh campaign
          exists, and self-gates on /auth/* and on a missing token. */}
      <EntryPopup />
      </GuidedTourProvider>
    </Router>
  );
};

export default AppRoutes;
